import { useCallback, useMemo } from 'react';
import type { Row } from 'react-table';

import type { DuplicateLineItem } from '../../../../../DuplicateAdsStepperProvider/types';
import { getUpdatedInitialLineItems, isDifferentSelectionFromSaved } from './utils';

export type UseConfirmLineItemsProps = {
  selectedRowIds: Record<string, boolean>;
  selectedSavedRowIds: Record<string, boolean>;
  selectedFlatRows: Row<DuplicateLineItem>[];
  rows: Row<DuplicateLineItem>[];
  lineItemsWithInitiallyDeselected: DuplicateLineItem[];
  handleClearSearch: () => void;
  replaceLineItems: (newLineItems: DuplicateLineItem[]) => void;
  adId: string;
};

export type UseConfirmLineItems = {
  isDifferentSelection: boolean;
  handleConfirm: () => void;
};

export const useConfirmLineItems = ({
  selectedRowIds,
  selectedFlatRows,
  handleClearSearch,
  replaceLineItems,
  selectedSavedRowIds,
  lineItemsWithInitiallyDeselected,
  rows,
  adId,
}: UseConfirmLineItemsProps): UseConfirmLineItems => {
  const isDifferentSelection = useMemo(() => {
    const selectedRowIdsKeys = Object.keys(selectedRowIds);
    const selectedSavedRowIdsKeys = Object.keys(selectedSavedRowIds);

    return isDifferentSelectionFromSaved(selectedRowIdsKeys, selectedSavedRowIdsKeys);
  }, [selectedRowIds, selectedSavedRowIds]);

  const handleConfirm = useCallback((): void => {
    const initialLineItems = getUpdatedInitialLineItems({
      selectedRowIds,
      lineItemsWithInitiallyDeselected,
      adId,
      rows,
    });

    const newLineItems: DuplicateLineItem[] = [...initialLineItems];

    // for selected line items in the search table,
    // push them into the array, but avoid duplicates of line items that were already pre-selected
    selectedFlatRows.forEach(({ original: lineItem }) => {
      if (!initialLineItems.find(({ id }) => id === lineItem.id))
        newLineItems.push({
          ...lineItem,
          selected: true,
          initiallyDeselectedForAdIds: [],
        });
    });

    replaceLineItems(newLineItems);
    handleClearSearch();
  }, [
    adId,
    handleClearSearch,
    lineItemsWithInitiallyDeselected,
    replaceLineItems,
    rows,
    selectedFlatRows,
    selectedRowIds,
  ]);

  return {
    isDifferentSelection,
    handleConfirm,
  };
};
